import { plural, t } from '@lingui/core/macro';
import type { BrandInsightsPartnerRecord, BrandInsightsTotalsRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { MonthNavigator, NoddiButton, NoddiCard, NoddiColumnDef, NoddiStaticTable } from 'noddi-ui-web';
import { CustomDateFormats, getStartOfMonth, NoddiDateFormats, noddiFormatDate } from 'noddi-util';

import { ApiErrorMessageWithTrans } from '../../components/Error/ApiErrorMessageWithTrans';
import { translateSalesItemCategory } from '../../utils/translateBookingCategory';
import { formatCount, formatMoney, formatShare } from './brandInsightsFormat';
import { usePartnerMonthParam } from './usePartnerMonthParam';

interface Props {
  insightsToken: string;
  /** Partners and totals of the page period, shown until a month is picked; undefined while the period loads. */
  periodPartners?: BrandInsightsPartnerRecord[];
  periodTotals?: BrandInsightsTotalsRecord;
  periodLabel: string;
}

export const BrandInsightsPartners = ({ insightsToken, periodPartners, periodTotals, periodLabel }: Props) => {
  const [month, setMonth] = usePartnerMonthParam();

  const {
    data: monthData,
    isLoading: isMonthLoading,
    isRefetching: isMonthRefetching,
    error: monthError,
    refetch: refetchMonth
  } = noddiAsync.useGet({
    type: URLKeys.getBrandInsightsPartners,
    input: {
      insightsToken,
      month: month ? noddiFormatDate(getStartOfMonth(month), NoddiDateFormats.API_DATE) : ''
    },
    queryConfig: { enabled: Boolean(month) }
  });

  const partners = (month ? monthData?.partners : periodPartners) ?? [];
  const totals = month ? monthData?.totals : periodTotals;
  const scopeLabel = month ? noddiFormatDate(month, CustomDateFormats.FULL_MONTH_AND_YEAR) : periodLabel;
  const bookingsLabel = totals ? plural(totals.bookings, { one: '# booking', other: '# bookings' }) : '';
  const revenue = totals ? formatMoney(totals.revenue) : '';

  return (
    <NoddiCard
      title={t`Service partners`}
      description={totals ? t`${scopeLabel} · ${bookingsLabel}, ${revenue}` : scopeLabel}
      headerNode={
        month ? (
          <div className='flex flex-wrap items-center gap-2'>
            <MonthNavigator value={month} onChange={setMonth} />
            <NoddiButton variant='ghost' onClick={() => setMonth(undefined)}>
              {t`Whole period`}
            </NoddiButton>
          </div>
        ) : (
          <NoddiButton variant='outline' startIcon='Calendar' onClick={() => setMonth(getStartOfMonth(new Date()))}>
            {t`Per month`}
          </NoddiButton>
        )
      }
      contentClassName='p-0'
    >
      {monthError ? (
        <div className='p-5'>
          <ApiErrorMessageWithTrans
            error={monthError}
            isRetrying={isMonthRefetching}
            onRetry={() => void refetchMonth()}
          />
        </div>
      ) : (
        <NoddiStaticTable
          data={partners}
          columns={getPartnerColumns(totals?.revenue.amount ?? 0)}
          getRowId={(row) => String(row.serviceOrganizationId)}
          isPending={month ? isMonthLoading : !periodPartners}
          enablePagination={false}
          enableSearch={false}
          hideColumnToggle
          hideRowCount
          hideCopyRowId
        />
      )}
    </NoddiCard>
  );
};

const getPartnerColumns = (totalRevenue: number): NoddiColumnDef<BrandInsightsPartnerRecord>[] => [
  {
    header: t`Partner`,
    accessorKey: 'name',
    cell: ({ row }) => (
      <span className='flex items-center gap-2'>
        {row.original.logo ? (
          // Decorative: the name beside it already says who the partner is.
          <img src={row.original.logo.url} alt='' className='h-6 w-auto max-w-16 shrink-0 object-contain' />
        ) : null}
        {row.original.name}
      </span>
    )
  },
  {
    header: t`Bookings`,
    accessorKey: 'bookings',
    cell: ({ row }) => formatCount(row.original.bookings)
  },
  {
    header: t`Revenue`,
    accessorFn: (row) => row.revenue.amount,
    cell: ({ row }) => formatMoney(row.original.revenue)
  },
  {
    header: t`Share of revenue`,
    accessorFn: (row) => row.revenue.amount,
    cell: ({ row }) => formatShare(row.original.revenue.amount, totalRevenue)
  },
  {
    header: t`Top service`,
    accessorKey: 'topCategory',
    cell: ({ row }) => (row.original.topCategory ? translateSalesItemCategory(row.original.topCategory) : null)
  }
];
